import posthog from "posthog-js"
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from "react"
import { getPostHogHost, POSTHOG_DEFAULTS } from "./posthog.config"

interface PostHogContextInterface {
  posthog: typeof posthog | null
}

const PostHogContext = createContext<PostHogContextInterface>({
  posthog: null,
})

interface PostHogProviderProps {
  apiKey: string
  enabled?: boolean
  children: ReactNode
}

const PostHogProvider = ({
  apiKey,
  enabled = true,
  children,
}: PostHogProviderProps) => {
  const initialized = useRef(false)

  useEffect(() => {
    if (!enabled) {
      if (initialized.current) {
        posthog.opt_out_capturing()
        posthog.reset()
      }
      return
    }

    if (initialized.current) {
      posthog.opt_in_capturing()
      return
    }

    if (!apiKey) {
      console.warn("PostHog disabled: VITE_POSTHOG_KEY is not set")
      return
    }

    initialized.current = true
    const environment = import.meta.env.VITE_ENV

    posthog.init(apiKey, {
      api_host: getPostHogHost(),
      defaults: POSTHOG_DEFAULTS,
      persistence: "localStorage",
      capture_pageview: false,
      capture_pageleave: true,
      ip: false,
      person_profiles: "identified_only",
      capture_exceptions: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "*",
      },
      loaded: (ph) => {
        ph.register({ environment })
        if (environment === "dev") {
          ph.debug()
        }
      },
    })
  }, [apiKey, enabled])

  return (
    <PostHogContext.Provider value={{ posthog: enabled ? posthog : null }}>
      {children}
    </PostHogContext.Provider>
  )
}

const usePostHog = () => {
  const { posthog } = useContext(PostHogContext)
  return posthog
}

export { PostHogContext, PostHogProvider, usePostHog }
